<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinancePaymentResponsibilityGroup from './RealEstateFinancePaymentResponsibilityGroup.vue';
import RealEstateFinancePaymentSummary from './RealEstateFinancePaymentSummary.vue';

const props = defineProps({
  canManagePayers: { type: Boolean, default: false },
  installment: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  responsibilities: { type: Array, default: () => [] },
});

const emit = defineEmits(['boleto', 'edit', 'payment']);
const { t } = useI18n();
const expanded = ref(true);
const roles = computed(() =>
  ['Vendedor', 'Comprador'].filter(role =>
    props.responsibilities.some(responsibility => responsibility.papel === role)
  )
);
</script>

<template>
  <section
    data-testid="finance-payment-responsibilities"
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <h3 class="font-semibold text-lais-text">
          {{ t('REAL_ESTATE_FINANCE.DETAIL.PAYERS') }}
        </h3>
        <span class="text-xs text-lais-text-muted">
          {{
            t('REAL_ESTATE_FINANCE.DETAIL.PAYER_COUNT', {
              count: responsibilities.length,
            })
          }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <LaisPrimaryButton
          v-if="canManagePayers"
          size="sm"
          variant="ghost"
          :disabled="loading"
          @click="emit('edit')"
        >
          {{ t('REAL_ESTATE_FINANCE.DETAIL.EDIT_PAYERS') }}
        </LaisPrimaryButton>
        <button
          data-toggle-payment-responsibilities
          type="button"
          class="rounded-lais p-2 text-lais-text-muted hover:bg-lais-bg-soft hover:text-lais-text"
          :aria-expanded="expanded"
          :aria-label="
            expanded
              ? t('REAL_ESTATE_FINANCE.DETAIL.COLLAPSE_PAYERS')
              : t('REAL_ESTATE_FINANCE.DETAIL.EXPAND_PAYERS')
          "
          @click="expanded = !expanded"
        >
          <span
            :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>
      </div>
    </div>
    <p
      v-if="!responsibilities.length"
      class="mt-3 text-sm text-lais-text-muted"
    >
      {{ t('REAL_ESTATE_FINANCE.DETAIL.NO_PAYERS') }}
    </p>
    <div v-else-if="expanded" class="mt-4 flex flex-col gap-4">
      <RealEstateFinancePaymentSummary
        :installment="installment"
        :responsibilities="responsibilities"
      />
      <RealEstateFinancePaymentResponsibilityGroup
        v-for="role in roles"
        :key="role"
        :loading="loading"
        :responsibilities="responsibilities"
        :role="role"
        @boleto="emit('boleto', $event)"
        @payment="emit('payment', $event)"
      />
    </div>
  </section>
</template>
